/**
 * The paging and multi-value filters shared by every dynamic "get all" endpoint.
 *
 * The API binds one `PagingFilter` across the entry, order and exhibitor
 * listings, so the rules below hold for all of them.
 */

import { PAGINATION } from '../../../core/constants/pagination.constants';

/** What a caller wants back: one page, or the whole set. */
export interface PagingRequest {
  page?: number;
  pageSize?: number;
  /** Ask for every row. The API then ignores `page`/`pageSize` entirely. */
  includeAll?: boolean;
  sortBy?: string;
  sortDirection?: string;
}

/**
 * The paging half of a request body.
 *
 * A caller that wants everything sends `includeAll: true` rather than an
 * arbitrarily large `pageSize` — a cap picked by hand is a silent limit, and
 * the set only has to outgrow it once for rows to start disappearing.
 */
export function toPagingBody(paging: PagingRequest): Record<string, unknown> {
  return {
    ...(paging.includeAll
      ? { includeAll: true }
      : { page: paging.page ?? PAGINATION.DEFAULT_PAGE, pageSize: paging.pageSize ?? PAGINATION.DEFAULT_PAGE_SIZE }),
    ...(paging.sortBy ? { sortBy: paging.sortBy } : {}),
    ...(paging.sortDirection ? { sortDirection: paging.sortDirection } : {})
  };
}

/**
 * A status filter as the API reads it.
 *
 * `status` and `paymentStatus` are bound from a JSON **array**; a bare string
 * is skipped without complaint, so the filter silently does nothing. Blank
 * values are dropped so an unset filter stays absent rather than matching
 * nothing.
 */
export function toStatusList(value: string | string[] | null | undefined): string[] | undefined {
  const values = (Array.isArray(value) ? value : [value])
    .map((entry) => entry?.trim())
    .filter((entry): entry is string => !!entry);

  return values.length ? values : undefined;
}

/** An order-number filter as the API binds it: a number, or `null` when the
 *  box is blank or not numeric — a string like `""` fails the int binding. */
export function toOrderNumber(value: number | string | null | undefined): number | null {
  const text = String(value ?? '').trim();
  if (!text) {
    return null;
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}
